/**
 * components/toastConfig.tsx
 * Premium renderers for react-native-toast-message (ported from SK Catalog's
 * components/toastConfig.tsx). Same card shape, accent rail, icon tile and
 * shadow — but wired to SK Hospitality's own theme tokens (useTheme) so it
 * flips with the app's light/dark mode instead of a pinned palette.
 */
import React from 'react';
import { View, Text, Platform } from 'react-native';
import type { ToastConfig, ToastConfigParams } from 'react-native-toast-message';
import { useTheme } from '../theme';
import Icon from './Icon';

const isWeb = Platform.OS === 'web';

// Title 13 / subtitle 12, semibold — matches our fontSize scale.
const FS = { md: 13, sm: 12 };
const SEMIBOLD = '600' as const;

type Tone = 'success' | 'error' | 'info';

function ToastItem({ tone, text1, text2 }: { tone: Tone; text1?: string; text2?: string }) {
  const t = useTheme();
  const c = t.colors;

  const cfg =
    tone === 'success'
      ? { fg: c.success, soft: c.successSoft, line: c.successLine, icon: 'check' }
      : tone === 'info'
        ? { fg: c.info, soft: c.infoSoft, line: c.infoLine, icon: 'info' }
        : { fg: c.danger, soft: c.dangerSoft, line: c.dangerLine, icon: 'warn' };

  return (
    <View
      style={{
        flexDirection: 'row', alignItems: 'flex-start', gap: 12, width: '92%', maxWidth: 440,
        paddingVertical: 13, paddingHorizontal: 14, borderRadius: 14, overflow: 'hidden',
        backgroundColor: c.card, borderWidth: 1, borderColor: c.line,
        ...(isWeb
          ? ({ boxShadow: '0 18px 44px -12px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.25)', zIndex: 999999 } as any)
          : { elevation: 14, shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: 16, shadowOffset: { width: 0, height: 8 } }),
      }}
    >
      {/* accent rail */}
      <View style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: cfg.fg }} />
      <View style={{ width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: cfg.soft, borderWidth: 1, borderColor: cfg.line, marginLeft: 2 }}>
        <Icon name={cfg.icon} size={16} color={cfg.fg} strokeWidth={1.9} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        {!!text1 && <Text style={{ fontSize: FS.md, fontWeight: SEMIBOLD, color: c.ink, letterSpacing: -0.1, fontFamily: t.font.sans }}>{text1}</Text>}
        {!!text2 && <Text style={{ fontSize: FS.sm, color: c.ink3, marginTop: 2, lineHeight: FS.sm * 1.45, fontFamily: t.font.sans }}>{text2}</Text>}
      </View>
    </View>
  );
}

export const toastConfig: ToastConfig = {
  success: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="success" text1={text1} text2={text2} />,
  error: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="error" text1={text1} text2={text2} />,
  info: ({ text1, text2 }: ToastConfigParams<any>) => <ToastItem tone="info" text1={text1} text2={text2} />,
};
